import moment from 'moment'

// Form model for a crew-created job (self-dispatched dedicated duties): the
// blank form, the form rebuilt from an existing journey, validation and the
// API payload. Pure functions, no UI.
//
// Keep in sync with mobile/src/utils/ptsCrewJobForm.js (manually mirrored
// pair) and with the server rules in
// server/src/routers/patientTransport/crewRouters/jobs.js.

// The outstanding-details labels moved to ../outstandingLabels.js when
// reduced-minimum-dataset bookings started sharing them; re-exported here so
// the crew-view imports stay as they were.
export {
  OUTSTANDING_LABELS,
  describeOutstanding,
} from '../outstandingLabels.js'

const ADDRESS_FIELDS = ['addressLine1', 'addressLine2', 'town', 'city', 'postCode']

const VISIT_ONLY_TYPE = 'Risk Assessment'
// Journey types that normally start at the patient's home
const HOME_PICKUP_TYPES = ['Admission', 'Outpatient', VISIT_ONLY_TYPE]

export const isVisitOnlyType = (journeyType) => journeyType === VISIT_ONLY_TYPE

const asId = (value) => String(value?._id || value || '')

export const emptyAddress = () => ({
  addressLine1: '',
  addressLine2: '',
  town: '',
  city: '',
  postCode: '',
  coordinates: null,
  addressMeta: null,
})

export const hasAddress = (address) =>
  !!(address?.addressLine1?.trim() || address?.postCode?.trim())

export const formatAddress = (address) =>
  ADDRESS_FIELDS.map((field) => address?.[field])
    .filter(Boolean)
    .join(', ')

// An address picked from the /pts/address-search results
export const addressFromOption = (option = {}) => ({
  addressLine1: option.addressLine1 || '',
  addressLine2: option.addressLine2 || '',
  town: option.town || '',
  city: option.city || '',
  postCode: option.postCode || '',
  coordinates:
    Array.isArray(option.coordinates) && option.coordinates.length === 2
      ? option.coordinates
      : null,
  addressMeta: option.addressMeta || null,
})

const addressFromRecord = (record = {}) => ({
  ...ADDRESS_FIELDS.reduce(
    (address, field) => ({ ...address, [field]: record?.[field] || '' }),
    {},
  ),
  coordinates:
    Array.isArray(record?.location?.coordinates) &&
    record.location.coordinates.length === 2
      ? record.location.coordinates
      : null,
  addressMeta: record?.addressMeta || null,
})

// kind: 'hospital' (the duty's own hospital) | 'otherHospital' |
// 'patientHome' | 'other' | 'pending' (destination not known yet)
export const emptyStop = (kind) => ({
  kind,
  organisation: null,
  ward: '',
  name: '',
  address: emptyAddress(),
  note: '',
})

export const emptyPatient = () => ({
  firstName: '',
  lastName: '',
  dob: '',
  nhsNumber: '',
  hospitalNumber: '',
  phoneNumber1: '',
  mobilityCategory: '',
  isBariatric: false,
  address: emptyAddress(),
  clinicalNotes: '',
})

export const defaultStopKinds = (journeyType) =>
  HOME_PICKUP_TYPES.includes(journeyType)
    ? { pickup: 'patientHome', dropoff: 'hospital' }
    : { pickup: 'hospital', dropoff: 'pending' }

export const newJobForm = () => ({
  journeyType: 'Discharge',
  wardDepartment: '',
  priority: 'Routine',
  pickup: emptyStop('hospital'),
  dropoff: emptyStop('pending'),
  // 'later' | 'new' | 'existing'
  patientMode: 'later',
  existingPatient: null,
  patient: emptyPatient(),
  bookingNotes: '',
  // Which open run (route) the job travels with: a route id, '' for a
  // separate run, or null while the crew still have to choose
  joinRouteId: null,
})

// One open run: assume the new job goes with it (the crew can change it).
// Several: make the crew choose. None: it is a run of its own.
export const defaultJoinRouteId = (openRuns = []) => {
  if (openRuns.length === 1) return String(openRuns[0].routeId)
  if (openRuns.length > 1) return null
  return ''
}

export const describeOpenRun = (run) =>
  (run?.journeys || [])
    .map((journey) => `${journey.patientName} (${journey.journeyType})`)
    .join(', ')

// Same shape as the server's crewPatientSummary (patient-lookup results)
export const patientSummaryFromRecord = (patient, organisationID) => ({
  _id: patient?._id,
  patientID: patient?.patientID,
  firstName: patient?.demographics?.firstName || '',
  lastName: patient?.demographics?.lastName || '',
  dob: patient?.demographics?.dob || null,
  nhsNumber: patient?.demographics?.nhsNumber || '',
  hospitalNumber:
    (patient?.demographics?.hospitalNumbers || []).find(
      (entry) => asId(entry?.organisation) === asId(organisationID),
    )?.mrn || '',
  address: ADDRESS_FIELDS.reduce(
    (address, field) => ({
      ...address,
      [field]: patient?.demographics?.[field] || '',
    }),
    {},
  ),
  hasAddress: hasAddress(patient?.demographics),
  mobility: {
    category: patient?.mobility?.category || '',
    isBariatric: !!patient?.mobility?.isBariatric,
  },
})

const patientFormFromRecord = (patient, organisationID) => {
  const summary = patientSummaryFromRecord(patient, organisationID)
  return {
    ...emptyPatient(),
    firstName: summary.firstName,
    lastName: summary.lastName,
    dob: summary.dob ? moment(summary.dob).format('YYYY-MM-DD') : '',
    nhsNumber: summary.nhsNumber,
    hospitalNumber: summary.hospitalNumber,
    phoneNumber1: patient?.demographics?.phoneNumber1 || '',
    mobilityCategory: summary.mobility.category,
    isBariatric: summary.mobility.isBariatric,
    address: addressFromRecord(patient?.demographics),
    clinicalNotes: patient?.clinicalNotes || '',
  }
}

const stopFromJourney = (stop, organisationID, requestingWardID) => {
  if (!stop) return emptyStop('hospital')
  if (stop.pending) return emptyStop('pending')

  const note = stop.note || ''
  if (stop.locationType === 'Hospital') {
    const stopOrganisationID = asId(
      stop.organisationAsAddress ?? stop.when?.organisationAsAddress,
    )
    const ownHospital =
      !stopOrganisationID || stopOrganisationID === asId(organisationID)
    const wardID = asId(stop.wardDepartmentAsAddress)
    return {
      ...emptyStop(ownHospital ? 'hospital' : 'otherHospital'),
      organisation: ownHospital
        ? null
        : { _id: stopOrganisationID, orgName: stop.name || 'Hospital' },
      // A stop on the requesting ward is stored as "follows the requesting
      // ward" so it moves with it
      ward: ownHospital && wardID === asId(requestingWardID) ? '' : wardID,
      note,
    }
  }
  if (stop.locationType === 'Patient Address') {
    return { ...emptyStop('patientHome'), note }
  }
  return {
    ...emptyStop('other'),
    name: stop.name || '',
    address: addressFromRecord(stop),
    note,
  }
}

// The edit form for a job the crew already added (a journey from the crew
// dashboard, with its booking + patient populated)
export const jobFormFromJourney = (journey) => {
  const booking = journey?.booking || {}
  const organisationID = booking.organisation
  const patient = booking.patient || journey?.patient
  const owned = !!journey?.crewJob?.patientOwned
  const placeholder = !!patient?.placeholder
  const base = newJobForm()

  return {
    ...base,
    // An existing job is already on its run — moving it is control's call
    joinRouteId: '',
    journeyType: journey?.journeyType || booking.journeyType || base.journeyType,
    wardDepartment: asId(booking.wardDepartment),
    priority: journey?.priority || base.priority,
    pickup: stopFromJourney(
      journey?.pickup,
      organisationID,
      booking.wardDepartment,
    ),
    dropoff: stopFromJourney(
      journey?.dropoff,
      organisationID,
      booking.wardDepartment,
    ),
    patientMode: owned ? (placeholder ? 'later' : 'new') : 'existing',
    existingPatient: owned
      ? null
      : patientSummaryFromRecord(patient, organisationID),
    patient:
      owned && !placeholder
        ? patientFormFromRecord(patient, organisationID)
        : {
            ...emptyPatient(),
            mobilityCategory: owned ? '' : patient?.mobility?.category || '',
            isBariatric: owned ? false : !!patient?.mobility?.isBariatric,
          },
    bookingNotes: booking.bookingNotes || '',
  }
}

// The server only searches on a hospital number, an NHS number, or a
// surname together with a date of birth
export const canSearchPatient = (patient) =>
  !!(
    patient?.hospitalNumber?.trim() ||
    patient?.nhsNumber?.trim() ||
    (patient?.lastName?.trim() && patient?.dob)
  )

export const patientSearchCriteria = (patient) => ({
  hospitalNumber: patient?.hospitalNumber?.trim() || '',
  nhsNumber: patient?.nhsNumber?.trim() || '',
  lastName: patient?.lastName?.trim() || '',
  dob: patient?.dob || null,
})

export const patientSearchKey = (patient) =>
  JSON.stringify(patientSearchCriteria(patient))

const stopLabel = (side) => (side === 'pickup' ? 'pickup' : 'destination')

const patientHomeAvailable = (form) => {
  if (form.patientMode === 'new') return hasAddress(form.patient.address)
  if (form.patientMode === 'existing') {
    return !!form.existingPatient?.hasAddress || hasAddress(form.patient.address)
  }
  return false
}

// Returns the first problem as a message for the crew, or null
export const validateJobForm = ({ form, options }) => {
  if (!form.journeyType) return 'Select the type of job.'
  if ((options?.openRuns || []).length && form.joinRouteId === null) {
    return 'Choose whether this patient travels with your current job or on a separate run.'
  }
  if ((options?.wards || []).length && !form.wardDepartment) {
    return 'Select the requesting ward or department.'
  }

  if (form.patientMode === 'new') {
    if (!form.patient.firstName.trim() || !form.patient.lastName.trim()) {
      return "Enter the patient's first name and surname, or choose to add the patient later."
    }
    if (form.patient.dob && moment(form.patient.dob).isAfter(moment())) {
      return 'The date of birth cannot be in the future.'
    }
  }
  if (form.patientMode === 'existing' && !form.existingPatient?._id) {
    return 'Choose a patient from the search results, or add them as a new patient.'
  }

  const sides = isVisitOnlyType(form.journeyType)
    ? ['pickup']
    : ['pickup', 'dropoff']
  for (const side of sides) {
    const stop = form[side]
    if (side === 'pickup' && stop.kind === 'pending') {
      return 'Select where the patient is being collected from.'
    }
    if (stop.kind === 'otherHospital' && !stop.organisation?._id) {
      return `Select the hospital for the ${stopLabel(side)}.`
    }
    if (stop.kind === 'other' && !hasAddress(stop.address)) {
      return `Enter an address for the ${stopLabel(side)}.`
    }
    if (stop.kind === 'patientHome' && !patientHomeAvailable(form)) {
      return `The ${stopLabel(side)} is the patient's home — add the patient and their home address first.`
    }
  }

  return null
}

const addressPayload = (address) => ({
  ...ADDRESS_FIELDS.reduce(
    (fields, field) => ({ ...fields, [field]: (address?.[field] || '').trim() }),
    {},
  ),
  ...(address?.coordinates ? { location: { coordinates: address.coordinates } } : {}),
  ...(address?.addressMeta ? { addressMeta: address.addressMeta } : {}),
})

const stopPayload = (stop) => {
  const note = stop.note?.trim() ? { note: stop.note.trim() } : {}
  switch (stop.kind) {
    case 'pending':
      return { pending: true }
    case 'hospital':
      return {
        locationType: 'Hospital',
        ...(stop.ward ? { wardDepartmentAsAddress: stop.ward } : {}),
        ...note,
      }
    case 'otherHospital':
      return {
        locationType: 'Hospital',
        organisationAsAddress: stop.organisation?._id,
        ...(stop.ward ? { wardDepartmentAsAddress: stop.ward } : {}),
        ...note,
      }
    case 'patientHome':
      return { locationType: 'Patient Address', ...note }
    default:
      return {
        locationType: 'Other',
        ...(stop.name?.trim() ? { name: stop.name.trim() } : {}),
        ...addressPayload(stop.address),
        ...note,
      }
  }
}

const patientPayload = (form) => {
  const patient = form.patient
  const mobility = {
    ...(patient.mobilityCategory ? { category: patient.mobilityCategory } : {}),
    isBariatric: !!patient.isBariatric,
  }

  if (form.patientMode === 'existing' && form.existingPatient?._id) {
    // A matched patient: only the gaps the crew can fill
    return {
      mode: 'existing',
      existingId: form.existingPatient._id,
      details: {
        mobility,
        ...(hasAddress(patient.address)
          ? { address: addressPayload(patient.address) }
          : {}),
      },
    }
  }

  if (form.patientMode === 'new') {
    return {
      mode: 'new',
      details: {
        firstName: patient.firstName.trim(),
        lastName: patient.lastName.trim(),
        dob: patient.dob || null,
        nhsNumber: patient.nhsNumber.trim(),
        hospitalNumber: patient.hospitalNumber.trim(),
        phoneNumber1: patient.phoneNumber1.trim(),
        mobility,
        address: addressPayload(patient.address),
        clinicalNotes: patient.clinicalNotes.trim(),
      },
    }
  }

  return undefined
}

// A hospital stop with no ward of its own sits on the requesting ward, so it
// has to be re-sent when the requesting ward changes
const followsRequestingWard = (stop) => stop.kind === 'hospital' && !stop.ward

// `dirty` (edit only) names the parts the crew actually touched —
// { patient, pickup, dropoff, ward, notes, priority } — so an edit only
// sends, and the audit trail only records, what changed.
export const buildJobPayload = ({ form, mode, dirty = {}, clientRequestId }) => {
  const visitOnly = isVisitOnlyType(form.journeyType)
  const patient = patientPayload(form)

  if (mode === 'create') {
    return {
      clientRequestId,
      journeyType: form.journeyType,
      wardDepartment: form.wardDepartment || undefined,
      priority: form.priority,
      pickup: stopPayload(form.pickup),
      ...(visitOnly ? {} : { dropoff: stopPayload(form.dropoff) }),
      ...(patient ? { patient } : {}),
      bookingNotes: form.bookingNotes.trim(),
      ...(form.joinRouteId ? { joinRouteId: form.joinRouteId } : {}),
    }
  }

  const sendPickup =
    dirty.pickup || (dirty.ward && followsRequestingWard(form.pickup))
  const sendDropoff =
    !visitOnly &&
    (dirty.dropoff || (dirty.ward && followsRequestingWard(form.dropoff)))

  return {
    ...(dirty.patient && patient ? { patient } : {}),
    ...(dirty.ward ? { wardDepartment: form.wardDepartment || null } : {}),
    ...(sendPickup ? { pickup: stopPayload(form.pickup) } : {}),
    ...(sendDropoff ? { dropoff: stopPayload(form.dropoff) } : {}),
    ...(dirty.priority ? { priority: form.priority } : {}),
    ...(dirty.notes ? { bookingNotes: form.bookingNotes.trim() } : {}),
  }
}
